import { createSelector } from 'reselect';
import {
  prop,
  propOr,
  pipe,
  pathOr,
  find,
  pathEq,
  startsWith,
  path,
  match,
  nth,
  filter,
  map,
  ascend,
  sortWith,
  last,
} from 'ramda';
import moment from 'moment';

import i18n from 'services/i18n';
import { NA } from 'constants/common';
import { formatDate } from 'utilities/formatters';

const localState = prop('newestObservations');

export const getObservationById = id =>
  createSelector(
    localState,
    prop(id),
  );

export const getObservationLoadingStatus = id =>
  createSelector(
    getObservationById(id),
    propOr(true, 'loading'),
  );

export const getObservationData = id =>
  createSelector(
    getObservationById(id),
    prop('data'),
  );

export const getObservationShouldShowLoading = id =>
  createSelector(
    [getObservationLoadingStatus(id), getObservationData(id)],
    (loading, data) => loading && !data,
  );

export const getObservationEntryResource = id =>
  createSelector(
    getObservationData(id),
    pathOr(null, ['entry', '0', 'resource']),
  );

export const isObservationEntryResource = id =>
  createSelector(
    getObservationEntryResource(id),
    data => !!data,
  );

export const getObservationEntryResourceComponent = id =>
  createSelector(
    getObservationEntryResource(id),
    propOr([], 'component'),
  );

export const getNewestObservationBatteryLife = id =>
  createSelector(
    getObservationEntryResourceComponent(id),
    pipe(
      find(pathEq(['code', 'coding', '0', 'code'], 'batteryLife')),
      propOr(NA, 'valueString'),
    ),
  );

export const getNewestObservationPacingSettings = id =>
  createSelector(
    getObservationEntryResourceComponent(id),
    pipe(
      find(pathEq(['code', 'coding', '0', 'code'], 'pacing')),
      propOr(NA, 'valueString'),
    ),
  );

export const getNewestObservationShockSettings = id =>
  createSelector(
    getObservationEntryResourceComponent(id),
    pipe(
      find(pathEq(['code', 'coding', '0', 'code'], 'shock')),
      propOr(NA, 'valueString'),
    ),
  );

const filterShockDates = pipe(
  path(['code', 'coding', '0', 'code']),
  startsWith('shockDate'),
);

export const getNewestObservationReportItemShocks = id =>
  createSelector(
    getObservationEntryResourceComponent(id),
    component =>
      pipe(
        filter(filterShockDates),
        map(item => {
          const code = path(['code', 'coding', '0', 'code'], item);
          const itemId = pipe(
            match(/shockDate_(\d)/),
            nth(1),
          )(code);
          const dateTime = `${prop('valueDateTime', item)} ${pipe(
            find(pathEq(['code', 'coding', '0', 'code'], `shockHour_${itemId}`)),
            propOr('', 'valueTime'),
          )(component)}`;
          return { dateTime: moment(dateTime).valueOf() };
        }),
        sortWith([ascend(prop('dateTime'))]), // TODO: need best solution!
        last,
        item => (item ? formatDate(i18n.t('dateTimeFormat.lastShockDateTime'))(item.dateTime) : NA),
      )(component),
  );
